import { ref } from 'vue'

type Theme = 'dark' | 'light' | 'system'

const DARK_VARS: Record<string, string> = {
  '--bg': '#0d1117',
  '--surface': '#161b22',
  '--text': '#e6edf3',
  '--muted': '#8b949e',
  '--border': '#30363d',
  '--primary': '#4b7abf',
  '--primary2': '#6b9fd4',
}

const LIGHT_VARS: Record<string, string> = {
  '--bg': '#f6f7fb',
  '--surface': '#ffffff',
  '--text': '#1d1f2a',
  '--muted': '#6b7280',
  '--border': '#e5e7eb',
  '--primary': '#1f3a5f',
  '--primary2': '#4b7abf',
}

const theme  = ref<Theme>('system')
const isDark = ref(false)

function applyTheme(value: Theme) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const shouldBeDark = value === 'dark' || (value === 'system' && prefersDark)
  isDark.value = shouldBeDark

  // Actualizar clase (para variantes Tailwind dark:)
  document.documentElement.classList.toggle('dark',  shouldBeDark)
  document.documentElement.classList.toggle('light', !shouldBeDark)

  // Escribir variables directamente como inline style para que ganen sobre
  // el branding del sistema, que también usa style.setProperty()
  const vars = shouldBeDark ? DARK_VARS : LIGHT_VARS
  const style = document.documentElement.style
  for (const [key, val] of Object.entries(vars)) {
    style.setProperty(key, val)
  }
}

// Inicializa al cargar el módulo — antes de que monte cualquier componente
theme.value = (localStorage.getItem('theme') as Theme | null) ?? 'system'
applyTheme(theme.value)

// Reacciona a cambios del sistema operativo (solo en modo 'system')
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  if (theme.value === 'system') applyTheme('system')
})

export function useDarkMode() {
  function toggle() {
    const next: Theme = isDark.value ? 'light' : 'dark'
    theme.value = next
    localStorage.setItem('theme', next)
    applyTheme(next)
  }

  function setTheme(value: Theme) {
    theme.value = value
    localStorage.setItem('theme', value)
    applyTheme(value)
  }

  return { isDark, theme, toggle, setTheme }
}
